import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Download, Globe } from 'lucide-react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from './ui/navigation-menu';
import { Button } from './ui/button';

const Header = ({ scrollToSection }) => {
  const { t, language, toggleLanguage } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const kurumsalSubItems = [
    { key: 'hakkimizda', section: 'hakkimizda' },
    { key: 'sertifikalar', section: 'sertifikalar' },
    { key: 'surdurulebilirlik', section: 'surdurulebilirlik' },
    { key: 'sosyalSorumluluk', section: 'sosyalsorumluluk' },
    { key: 'referanslarimiz', section: 'referanslar' },
  ];

  const handleNavClick = (section) => {
    scrollToSection(section);
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div
            className="flex-shrink-0 cursor-pointer"
            onClick={() => handleNavClick('hero')}
          >
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center overflow-hidden border-2 border-amber-300 shadow-lg">
              <img
                src="/logo.png"
                alt="Kar Yemek Logo"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML =
                    '<span class="text-white font-bold text-lg">KY</span>';
                }}
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            <NavigationMenu>
              <NavigationMenuList>
                {/* Kurumsal Dropdown */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-gray-700 hover:text-amber-600 font-medium text-sm px-3 py-2 transition-colors">
                    {t('nav.kurumsal')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-48 gap-1 p-2 bg-white rounded-lg shadow-xl border border-gray-100">
                      {kurumsalSubItems.map((item) => (
                        <li key={item.key}>
                          <NavigationMenuLink asChild>
                            <button
                              onClick={() => handleNavClick(item.section)}
                              className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 rounded-md transition-colors"
                            >
                              {t(`kurumsal.${item.key}`)}
                            </button>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Other Nav Items */}
                <NavigationMenuItem>
                  <button
                    onClick={() => handleNavClick('hizmetler')}
                    className="text-gray-700 hover:text-amber-600 font-medium text-sm px-3 py-2 transition-colors"
                  >
                    {t('nav.hizmetler')}
                  </button>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <button
                    onClick={() => handleNavClick('grup')}
                    className="text-gray-700 hover:text-amber-600 font-medium text-sm px-3 py-2 transition-colors"
                  >
                    {t('nav.grupFaaliyetAlanlari')}
                  </button>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <button
                    onClick={() => handleNavClick('inovasyon')}
                    className="text-gray-700 hover:text-amber-600 font-medium text-sm px-3 py-2 transition-colors"
                  >
                    {t('nav.inovasyon')}
                  </button>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <button
                    onClick={() => handleNavClick('galeri')}
                    className="text-gray-700 hover:text-amber-600 font-medium text-sm px-3 py-2 transition-colors"
                  >
                    {t('nav.galeri')}
                  </button>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <button
                    onClick={() => handleNavClick('haberler')}
                    className="text-gray-700 hover:text-amber-600 font-medium text-sm px-3 py-2 transition-colors"
                  >
                    {t('nav.haberler')}
                  </button>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <button
                    onClick={() => handleNavClick('iletisim')}
                    className="text-gray-700 hover:text-amber-600 font-medium text-sm px-3 py-2 transition-colors"
                  >
                    {t('nav.iletisim')}
                  </button>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="ml-2 text-gray-600 hover:text-amber-600"
            >
              <Globe className="w-4 h-4 mr-1" />
              {language.toUpperCase()}
            </Button>

            {/* E-Katalog Button (Desktop) */}
            <Button
              asChild
              className="ml-3 bg-amber-500 hover:bg-amber-600 text-white font-medium shadow-md hover:shadow-lg transition-all"
            >
              <a
                href="/files/e-katalog.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download className="w-4 h-4 mr-2" />
                {t('nav.eKatalog')}
              </a>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="text-gray-600"
            >
              <Globe className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-100">
            <div className="space-y-2">
              {/* Kurumsal */}
              <div className="space-y-1">
                <p className="px-3 py-2 font-medium text-gray-800">
                  {t('nav.kurumsal')}
                </p>
                {kurumsalSubItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => handleNavClick(item.section)}
                    className="block w-full text-left px-6 py-2 text-sm text-gray-600 hover:bg-amber-50 hover:text-amber-600"
                  >
                    {t(`kurumsal.${item.key}`)}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handleNavClick('hizmetler')}
                className="block w-full text-left px-3 py-2 font-medium text-gray-700 hover:bg-amber-50"
              >
                {t('nav.hizmetler')}
              </button>
              <button
                onClick={() => handleNavClick('grup')}
                className="block w-full text-left px-3 py-2 font-medium text-gray-700 hover:bg-amber-50"
              >
                {t('nav.grupFaaliyetAlanlari')}
              </button>
              <button
                onClick={() => handleNavClick('inovasyon')}
                className="block w-full text-left px-3 py-2 font-medium text-gray-700 hover:bg-amber-50"
              >
                {t('nav.inovasyon')}
              </button>
              <button
                onClick={() => handleNavClick('galeri')}
                className="block w-full text-left px-3 py-2 font-medium text-gray-700 hover:bg-amber-50"
              >
                {t('nav.galeri')}
              </button>
              <button
                onClick={() => handleNavClick('haberler')}
                className="block w-full text-left px-3 py-2 font-medium text-gray-700 hover:bg-amber-50"
              >
                {t('nav.haberler')}
              </button>
              <button
                onClick={() => handleNavClick('iletisim')}
                className="block w-full text-left px-3 py-2 font-medium text-gray-700 hover:bg-amber-50"
              >
                {t('nav.iletisim')}
              </button>

              {/* E-Katalog Button (Mobile) */}
              <Button
                asChild
                className="w-full mt-4 bg-amber-500 hover:bg-amber-600 text-white"
              >
                <a
                  href="/files/e-katalog.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('nav.eKatalog')}
                </a>
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;